const DEFAULT_VISIBLE_TEAM_MEMBERS = 6;

export type SavedTeamMember = { user_id: string; is_visible: boolean };

export type UnseenTeamMember = SavedTeamMember & { position: number };

const isSavedTeamMember = (member: unknown): member is SavedTeamMember =>
  member != null &&
  typeof member === 'object' &&
  typeof (member as SavedTeamMember).user_id === 'string';

type TeamRow = {
  user_id: string;
  user: { show_in_agency_pages?: boolean | null } | null;
};

const resolveSavedOrder = <T extends { user_id: string }>(
  teamUsers: readonly T[],
  savedTeamMembers: unknown,
): Array<T & { is_visible: boolean }> => {
  const teamUsersById = new Map(teamUsers.map(tu => [tu.user_id, tu]));
  const saved = Array.isArray(savedTeamMembers)
    ? savedTeamMembers.filter(isSavedTeamMember).flatMap(member => {
        const teamUser = teamUsersById.get(member.user_id);
        return teamUser ? [{ ...teamUser, is_visible: member.is_visible }] : [];
      })
    : [];

  if (saved.length === 0) {
    return teamUsers.map((teamUser, index) => ({
      ...teamUser,
      is_visible: index < DEFAULT_VISIBLE_TEAM_MEMBERS,
    }));
  }

  const savedIds = new Set(saved.map(member => member.user_id));
  const added = teamUsers
    .filter(tu => !savedIds.has(tu.user_id))
    .map(tu => ({ ...tu, is_visible: false }));

  return [...saved, ...added];
};

export const resolveAgencyTeamMembers = <T extends TeamRow>(
  teamUsers: readonly T[],
  savedTeamMembers: unknown,
): {
  members: Array<T & { user: NonNullable<T['user']>; is_visible: boolean }>;
  unseen: UnseenTeamMember[];
} => {
  const resolved = resolveSavedOrder(
    teamUsers.filter(
      tu => tu.user == null || tu.user.show_in_agency_pages === true,
    ),
    savedTeamMembers,
  );
  return {
    members: resolved.filter(
      (
        member,
      ): member is T & { user: NonNullable<T['user']>; is_visible: boolean } =>
        member.user != null,
    ),
    unseen: resolved.flatMap(({ user, user_id, is_visible }, position) =>
      user == null ? [{ user_id, is_visible, position }] : [],
    ),
  };
};

export const toSavedTeamMembers = (
  members: ReadonlyArray<SavedTeamMember>,
): SavedTeamMember[] =>
  members.map(({ user_id, is_visible }) => ({ user_id, is_visible }));

export const mergeUnseenTeamMembers = (
  members: ReadonlyArray<SavedTeamMember>,
  unseen: ReadonlyArray<UnseenTeamMember>,
  unseenVisible?: boolean,
): SavedTeamMember[] => {
  const merged = toSavedTeamMembers(members);
  for (const { user_id, is_visible, position } of unseen) {
    merged.splice(position, 0, {
      user_id,
      is_visible: unseenVisible ?? is_visible,
    });
  }
  return merged;
};
